import React, { useState } from 'react'
import { Box, Button, InputLabel, MenuItem, Stack, Switch, TextField, Typography } from '@mui/material'
import { type SlotEmailSettings } from '../../../../models/SlotEmailSettings'
import { Controller, useWatch, type Control } from 'react-hook-form'
import { grey } from '../../../../colors'
import { NextEmailSendRow } from '../NextEmailSendRow'
import api from '../../../../utils/api'
import { notification } from '../../../../components/Notifications'
import { getTaskTriggerToastMessage } from '../../../../utils/taskTriggerToast'

interface EmailSettingsCardProps {
  name: string
  isReadOnly: boolean
  isSlotPublishedOrLater: boolean
  setting: SlotEmailSettings
  control: Control
  showDevTools?: boolean
  devTaskName?: string
  slotId?: string
}

const daysBeforeOptions = [
  { value: 1, label: '1 Day Before' },
  { value: 2, label: '2 Days Before' },
  { value: 3, label: '3 Days Before' },
  { value: 4, label: '4 Days Before' },
  { value: 5, label: '5 Days Before' },
  { value: 6, label: '6 Days Before' },
  { value: 7, label: '1 Week Before' },
  { value: 14, label: '2 Weeks Before' },
  { value: 21, label: '3 Weeks Before' },
  { value: 30, label: '1 Month Before' }
]

const reminderIntervalOptions = [
  { value: 1, label: 'Daily' },
  { value: 2, label: 'Every other day' },
  { value: 3, label: 'Every 3 days' },
  { value: 4, label: 'Every 4 days' },
  { value: 5, label: 'Every 5 days' },
  { value: 6, label: 'Every 6 days' },
  { value: 7, label: 'Weekly' },
  { value: 14, label: 'Every 2 weeks' },
  { value: 21, label: 'Every 3 weeks' },
  { value: 30, label: 'Monthly' }
]

const EmailSettingsCard: React.FC<EmailSettingsCardProps> = ({
  name,
  setting,
  isReadOnly,
  isSlotPublishedOrLater,
  control,
  showDevTools,
  devTaskName,
  slotId
}) => {
  const isEnabled = useWatch({ control, name: `${name}.enabled` }) ?? setting.enabled
  const [isTriggering, setIsTriggering] = useState(false)

  const handleTrigger = async (): Promise<void> => {
    if (!devTaskName) return
    setIsTriggering(true)
    try {
      const result = await api.post<{ taskId?: string, task_id?: string, message?: string }>(`/api/tasks/trigger/${devTaskName}/`, { slot_id: slotId })
      notification(getTaskTriggerToastMessage(result.data), { variant: 'success' })
    } catch {
      notification(`Failed to trigger task: ${devTaskName}`, { variant: 'error' })
    } finally {
      setIsTriggering(false)
    }
  }

  return (
    <Stack spacing={6}>
      <Stack spacing={2}>
        <Box display="flex" alignItems="center" gap={2}>
          <Controller
            name={`${name}.enabled`}
            control={control}
            render={({ field: { onChange, value } }) => (
              <Switch checked={value} onChange={onChange} disabled={isReadOnly} />
            )}
          />
          <Typography variant="p5" sx={{ fontWeight: 'bold' }}>{setting.title}</Typography>
          {showDevTools === true && devTaskName != null && (
            <Button variant="outlined" size="small" disabled={isTriggering} onClick={() => { void handleTrigger() }} sx={{ ml: 'auto' }}>
              {isTriggering ? 'Sending...' : 'Send email'}
            </Button>
          )}
        </Box>
        <Typography variant="p5">{setting.description}</Typography>
      </Stack>

      {setting.settingType === 'DAYS_BEFORE' && (
        <Box minHeight={72}>
          <InputLabel sx={{ marginBottom: 2, color: grey[950] }}>Schedule</InputLabel>
          <Controller
            name={`${name}.daysBefore`}
            control={control}
            render={({ field }) => (
              <TextField select fullWidth {...field} value={field.value ?? ''} disabled={isReadOnly}>
                {daysBeforeOptions.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </TextField>
            )}
          />
        </Box>
      )}

      {setting.settingType === 'REMINDER_INTERVAL' && (
        <Box minHeight={72}>
          <InputLabel sx={{ marginBottom: 2, color: grey[950] }}>Frequency</InputLabel>
          <Controller
            name={`${name}.reminderInterval`}
            control={control}
            render={({ field }) => (
              <TextField select fullWidth {...field} value={field.value ?? ''} disabled={isReadOnly}>
                {reminderIntervalOptions.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </TextField>
            )}
          />
        </Box>
      )}

      <NextEmailSendRow
        isSlotPublishedOrLater={isSlotPublishedOrLater}
        isEnabled={isEnabled}
        nextEmailPushTimestamp={setting.nextEmailPushTimestamp}
        lastEmailSentTimestamp={setting.lastEmailSentTimestamp}
      />

      {/* <Stack spacing={1}>
        <Box sx={{ fontSize: '0.9rem' }}>Custom Message</Box>
        <Controller
          name={`${name}.customMessage`}
          control={control}
          render={({ field }) => (
            <ReactQuill
              theme="snow"
              style={{ borderRadius: '4px !important' }}
              value={field.value}
              readOnly={isReadOnly}
              onChange={(e) => { if (!isReadOnly) field.onChange(e) }}
            />
          )}
        />
        <Box sx={{ fontSize: '0.9rem', color: grey[600] }}>(Optional) Leave blank if no custom message</Box>
      </Stack> */}
    </Stack>
  )
}

export default EmailSettingsCard
